import { createReducer, on } from '@ngrx/store';
import { WebSocketActions, RealTimePriceActions } from '../actions/websocket.actions';
import { 
  WebSocketConnectionStatus,
  PriceUpdateMessage,
  CompetitorPriceMessage,
  SystemAlertMessage
} from '../../shared/interfaces/websocket.interfaces';
import { ProductPriceData, PriceAlert, MarketMetrics } from '../../core/services/real-time-price.service';

export interface WebSocketState {
  // Connection State
  connectionStatus: WebSocketConnectionStatus;
  isConnecting: boolean;
  connectionError: any;
  lastConnected: number | null;
  
  // Subscription State
  subscribedProducts: string[];
  subscribedToDashboard: boolean;
  
  // Message Queue
  queuedMessages: any[];
  
  // Real-time Data
  lastPriceUpdates: PriceUpdateMessage[];
  lastCompetitorUpdate: CompetitorPriceMessage | null;
  systemAlerts: SystemAlertMessage[];
  
  // Performance Metrics
  messagesReceived: number;
  messagesPerSecond: number;
  lastMessageTimestamp: number;
}

export interface RealTimePriceState {
  // Product Price Data
  productPrices: { [productId: string]: ProductPriceData };
  
  // Alerts
  priceAlerts: PriceAlert[];
  activeAlertsCount: number;
  acknowledgedAlertsCount: number;
  
  // Market Data
  marketMetrics: MarketMetrics;
  
  // Monitoring Configuration
  monitoringConfig: any;
  
  // Connection Health
  connectionHealth: {
    isHealthy: boolean;
    lastCheck: number;
    issues: string[];
  };
  
  // Subscription State
  monitoredProducts?: string[];
  
  // Error State
  error: any;
  
  // Loading State
  loading?: boolean;
  isLoading?: boolean;
}

const initialWebSocketState: WebSocketState = {
  connectionStatus: {
    isConnected: false,
    connectionAttempts: 0,
    isReconnecting: false,
    queuedMessages: 0
  },
  isConnecting: false,
  connectionError: null,
  lastConnected: null,
  subscribedProducts: [],
  subscribedToDashboard: false,
  queuedMessages: [],
  lastPriceUpdates: [],
  lastCompetitorUpdate: null,
  systemAlerts: [],
  messagesReceived: 0,
  messagesPerSecond: 0,
  lastMessageTimestamp: 0
};

const initialRealTimePriceState: RealTimePriceState = {
  productPrices: {},
  priceAlerts: [],
  activeAlertsCount: 0,
  acknowledgedAlertsCount: 0,
  marketMetrics: {
    totalProducts: 0,
    pricesUpdatedToday: 0,
    averagePriceChange: 0,
    competitorUpdates: 0,
    activeAlerts: 0,
    marketTrend: 'neutral',
    volatilityIndex: 0
  },
  monitoringConfig: null,
  connectionHealth: {
    isHealthy: true,
    lastCheck: Date.now(),
    issues: []
  },
  monitoredProducts: [],
  error: null,
  isLoading: false
};

export const webSocketReducer = createReducer(
  initialWebSocketState,
  
  // Connection Management
  on(WebSocketActions.connect, (state) => ({
    ...state,
    isConnecting: true,
    connectionError: null
  })),
  
  on(WebSocketActions.connectSuccess, (state) => ({
    ...state,
    isConnecting: false,
    connectionError: null,
    lastConnected: Date.now()
  })),
  
  on(WebSocketActions.connectFailure, (state, { error }) => ({
    ...state,
    isConnecting: false,
    connectionError: error
  })),
  
  on(WebSocketActions.connectionStatusChanged, (state, { status }) => ({
    ...state,
    connectionStatus: status,
    isConnecting: false
  })),
  
  on(WebSocketActions.disconnect, (state) => ({
    ...state,
    connectionStatus: {
      ...state.connectionStatus,
      isConnected: false,
      isReconnecting: false
    },
    isConnecting: false
  })),
  
  // Subscription Management
  on(WebSocketActions.subscribeToProducts, (state, { productIds }) => ({
    ...state,
    subscribedProducts: [...new Set([...state.subscribedProducts, ...productIds])]
  })),
  
  on(WebSocketActions.unsubscribeFromProducts, (state, { productIds }) => ({
    ...state,
    subscribedProducts: state.subscribedProducts.filter(id => !productIds.includes(id))
  })),
  
  on(WebSocketActions.subscribeToDashboard, (state) => ({
    ...state,
    subscribedToDashboard: true
  })),
  
  on(WebSocketActions.unsubscribeFromDashboard, (state) => ({
    ...state,
    subscribedToDashboard: false
  })),
  
  // Real-time Message Handling
  on(WebSocketActions.priceUpdateReceived, (state, { updates }) => {
    const newMessagesReceived = state.messagesReceived + updates.length;
    const currentTime = Date.now();
    const timeDiff = currentTime - state.lastMessageTimestamp;
    const messagesPerSecond = timeDiff > 0 ? (updates.length / (timeDiff / 1000)) : 0;
    
    return {
      ...state,
      lastPriceUpdates: updates,
      messagesReceived: newMessagesReceived,
      messagesPerSecond,
      lastMessageTimestamp: currentTime
    };
  }),
  
  on(WebSocketActions.competitorPriceReceived, (state, { update }) => ({
    ...state,
    lastCompetitorUpdate: update,
    messagesReceived: state.messagesReceived + 1,
    lastMessageTimestamp: Date.now()
  })),
  
  on(WebSocketActions.systemAlertReceived, (state, { alert }) => ({
    ...state,
    systemAlerts: [alert, ...state.systemAlerts.slice(0, 49)], // Keep latest 50
    messagesReceived: state.messagesReceived + 1,
    lastMessageTimestamp: Date.now()
  })),
  
  // Message Queue Management
  on(WebSocketActions.queueMessage, (state, { event, data }) => ({
    ...state,
    queuedMessages: [...state.queuedMessages, { event, data, timestamp: Date.now() }]
  })),
  
  on(WebSocketActions.processMessageQueue, (state) => ({
    ...state,
    queuedMessages: []
  })),
  
  on(WebSocketActions.clearMessageQueue, (state) => ({
    ...state,
    queuedMessages: []
  })),
  
  // Error Handling
  on(WebSocketActions.webSocketError, (state, { error }) => ({
    ...state,
    connectionError: error
  })),
  
  on(WebSocketActions.clearWebSocketError, (state) => ({
    ...state,
    connectionError: null
  }))
);

export const realTimePriceReducer = createReducer(
  initialRealTimePriceState,
  
  // Product Price Management
  on(RealTimePriceActions.updateProductPrices, (state, { prices }) => {
    const productPrices = { ...state.productPrices };
    prices.forEach(price => {
      productPrices[price.productId] = price;
    });
    
    return {
      ...state,
      productPrices,
      isLoading: false
    };
  }),
  
  on(RealTimePriceActions.updateSingleProductPrice, (state, { productId, priceData }) => ({
    ...state,
    productPrices: {
      ...state.productPrices,
      [productId]: priceData
    }
  })),
  
  on(RealTimePriceActions.clearProductPrices, (state) => ({
    ...state,
    productPrices: {}
  })),
  
  // Competitor Price Management
  on(RealTimePriceActions.updateCompetitorPrices, (state, { productId, competitors }) => {
    const existingProduct = state.productPrices[productId];
    if (!existingProduct) return state;
    
    return {
      ...state,
      productPrices: {
        ...state.productPrices,
        [productId]: {
          ...existingProduct,
          competitors: competitors.map(comp => ({
            competitorId: comp.competitorId,
            competitorName: comp.competitorName,
            price: comp.price,
            previousPrice: comp.previousPrice,
            changePercent: comp.priceChange,
            marketplace: comp.marketplace,
            lastUpdated: comp.timestamp,
            isLowestPrice: false // Will be calculated by service
          }))
        }
      }
    };
  }),
  
  // Alert Management
  on(RealTimePriceActions.addPriceAlert, (state, { alert }) => {
    const existingIndex = state.priceAlerts.findIndex(a => a.id === alert.id);
    let updatedAlerts;
    
    if (existingIndex >= 0) {
      updatedAlerts = [...state.priceAlerts];
      updatedAlerts[existingIndex] = alert;
    } else {
      updatedAlerts = [alert, ...state.priceAlerts];
    }
    
    const activeCount = updatedAlerts.filter(a => !a.acknowledged).length;
    const acknowledgedCount = updatedAlerts.length - activeCount;
    
    return {
      ...state,
      priceAlerts: updatedAlerts,
      activeAlertsCount: activeCount,
      acknowledgedAlertsCount: acknowledgedCount
    };
  }),
  
  on(RealTimePriceActions.updatePriceAlerts, (state, { alerts }) => {
    const activeCount = alerts.filter(a => !a.acknowledged).length;
    const acknowledgedCount = alerts.length - activeCount;
    
    return {
      ...state,
      priceAlerts: alerts,
      activeAlertsCount: activeCount,
      acknowledgedAlertsCount: acknowledgedCount
    };
  }),
  
  on(RealTimePriceActions.acknowledgeAlert, (state, { alertId }) => {
    const updatedAlerts = state.priceAlerts.map(alert =>
      alert.id === alertId ? { ...alert, acknowledged: true } : alert
    );
    
    const activeCount = updatedAlerts.filter(a => !a.acknowledged).length;
    const acknowledgedCount = updatedAlerts.length - activeCount;
    
    return {
      ...state,
      priceAlerts: updatedAlerts,
      activeAlertsCount: activeCount,
      acknowledgedAlertsCount: acknowledgedCount
    };
  }),
  
  on(RealTimePriceActions.clearAcknowledgedAlerts, (state) => {
    const activeAlerts = state.priceAlerts.filter(alert => !alert.acknowledged);
    
    return {
      ...state,
      priceAlerts: activeAlerts,
      activeAlertsCount: activeAlerts.length,
      acknowledgedAlertsCount: 0
    };
  }),
  
  on(RealTimePriceActions.clearAllAlerts, (state) => ({
    ...state,
    priceAlerts: [],
    activeAlertsCount: 0,
    acknowledgedAlertsCount: 0
  })),
  
  // Market Metrics
  on(RealTimePriceActions.updateMarketMetrics, (state, { metrics }) => ({
    ...state,
    marketMetrics: metrics
  })),
  
  // Configuration
  on(RealTimePriceActions.updateMonitoringConfig, (state, { config }) => ({
    ...state,
    monitoringConfig: config
  })),
  
  // Subscription Management
  on(RealTimePriceActions.subscribeToProductMonitoring, (state, { productId }) => ({
    ...state,
    monitoredProducts: [...new Set([...(state.monitoredProducts || []), productId])]
  })),
  
  on(RealTimePriceActions.unsubscribeFromProductMonitoring, (state, { productId }) => ({
    ...state,
    monitoredProducts: (state.monitoredProducts || []).filter(id => id !== productId)
  })),
  
  on(RealTimePriceActions.subscribeToMultipleProducts, (state, { productIds }) => ({
    ...state,
    monitoredProducts: [...new Set([...(state.monitoredProducts || []), ...productIds])]
  })),
  
  // Error Handling
  on(RealTimePriceActions.realTimePriceError, (state, { error }) => ({
    ...state,
    error,
    isLoading: false
  })),
  
  on(RealTimePriceActions.clearRealTimePriceError, (state) => ({
    ...state,
    error: null
  }))
);

// Combined state interface for easier management
export interface RealtimeState {
  webSocket: WebSocketState;
  realTimePrice: RealTimePriceState;
}